import { computed } from 'vue';
import { getSlugFromPrice, PRICE_SLUGS_BY_REGION } from '@/composables/useSearchFiltersSetup';

/**
 * Quick filter pills, advanced filter panel actions and location apply handlers.
 */
export function useSearchFilterControls(deps) {
  const {
    t,
    route,
    router,
    regionId,
    filters,
    selectedLocations,
    activePillDropdown,
    showAdvancedFilters,
    showLocationDropdown,
    showLocationDropdownMobile,
    mobilePriceOptions,
    formatMobilePrice,
    trackFilterChange,
    performSearch,
  } = deps;

  const pillPriceOptionsForQuick = computed(() => {
    const region = regionId?.value || 'es';
    const slugs = PRICE_SLUGS_BY_REGION[region] || PRICE_SLUGS_BY_REGION.es;
    return [
      { value: null, slug: null, label: t('colivingSearch.advancedFilters.price.noLimit') },
      ...slugs.map((s) => ({ value: s.value, slug: s.slug, label: s.label })),
    ];
  });

  const pillPriceLabel = computed(() => {
    if (filters.value.maxPrice == null || filters.value.maxPrice === 0) {
      return t('colivingSearchBar.maxPrice');
    }
    const opt = pillPriceOptionsForQuick.value.find((o) => o.value === filters.value.maxPrice);
    return opt?.label || t('colivingSearchBar.maxPrice');
  });

  const pillStayOptions = computed(() => [
    { value: null, label: t('colivingSearch.pills.stayOptions.anyDuration') },
    { value: 1, label: t('colivingSearch.pills.stayOptions.upTo1') },
    { value: 3, label: t('colivingSearch.pills.stayOptions.upTo3') },
    { value: 6, label: t('colivingSearch.pills.stayOptions.upTo6') },
    { value: 12, label: t('colivingSearch.pills.stayOptions.upTo12') },
  ]);

  const pillStayLabel = computed(() => {
    if (!filters.value.maxMinimumStay) return t('colivingSearch.pills.duration');
    const opt = pillStayOptions.value.find((o) => o.value === filters.value.maxMinimumStay);
    return opt ? opt.label : t('colivingSearch.pills.duration');
  });

  const pillMoreCount = computed(() => {
    let count = 0;
    if (filters.value.proOnly) count++;
    if (filters.value.policies.pets) count++;
    if (filters.value.services.internet) count++;
    if (filters.value.services.cleaning) count++;
    if (filters.value.services.allIncluded) count++;
    count += (filters.value.providers || []).length;
    return count;
  });

  const pillMoreActive = computed(() => pillMoreCount.value > 0);

  function togglePillDropdown(name) {
    activePillDropdown.value = activePillDropdown.value === name ? null : name;
    if (activePillDropdown.value) showAdvancedFilters.value = false;
  }

  function selectPillPrice(value) {
    trackFilterChange({
      filterName: 'maxPrice',
      filterValue: value,
      ciudad: selectedLocations.value.join(',') || 'todas',
    });
    filters.value.maxPrice = value;
    activePillDropdown.value = null;

    const slug = getSlugFromPrice(value, regionId?.value);
    const cityParam = route.params?.city;
    const isEN = route.path.startsWith('/findyourcoliving');
    const base = isEN ? '/findyourcoliving' : '/encuentratucoliving';

    if (slug && cityParam) {
      router.push(`${base}/${cityParam}/${slug}`);
    } else if (slug && !cityParam) {
      router.push({ path: base, query: { maxPrice: String(value) } });
      performSearch(true);
    } else if (!slug && cityParam) {
      router.push(`${base}/${cityParam}`);
    } else {
      performSearch(true);
    }
  }

  function selectPillStay(value) {
    trackFilterChange({
      filterName: 'maxMinimumStay',
      filterValue: value,
      ciudad: selectedLocations.value.join(',') || 'todas',
    });
    filters.value.maxMinimumStay = value;
    activePillDropdown.value = null;
    performSearch(true);
  }

  function quickToggleFilter(type) {
    let filterValue;
    if (type === 'pets') {
      filters.value.policies.pets = !filters.value.policies.pets;
      filterValue = filters.value.policies.pets;
    }
    if (type === 'couples') {
      filters.value.policies.couples = !filters.value.policies.couples;
      filterValue = filters.value.policies.couples;
    }
    if (type === 'students') {
      filters.value.forStudents = !filters.value.forStudents;
      filterValue = filters.value.forStudents;
    }
    if (type === 'proOnly') {
      filters.value.proOnly = !filters.value.proOnly;
      filterValue = filters.value.proOnly;
    }

    trackFilterChange({
      filterName: type,
      filterValue,
      ciudad: selectedLocations.value.join(',') || 'todas',
    });

    performSearch(true);
  }

  function applyLocationFilter() {
    showLocationDropdown.value = false;
    performSearch();
  }

  function applyLocationFilterMobile() {
    showLocationDropdownMobile.value = false;
    performSearch();
  }

  function applyAdvancedFilters() {
    showAdvancedFilters.value = false;
    performSearch();
  }

  function getLocationDisplay() {
    if (selectedLocations.value.length === 0) return '';
    if (selectedLocations.value.length === 1) return selectedLocations.value[0];
    return selectedLocations.value.join(', ');
  }

  function getActiveFiltersCount() {
    let count = 0;
    if (filters.value.maxPrice !== null && filters.value.maxPrice !== undefined) count++;
    if (filters.value.policies.pets) count++;
    if (filters.value.policies.couples) count++;
    if (filters.value.maxMinimumStay !== null) count++;
    if (filters.value.services.internet) count++;
    if (filters.value.services.cleaning) count++;
    if (filters.value.services.allIncluded) count++;
    if (filters.value.providers.length > 0) count++;
    if (filters.value.forStudents) count++;
    if (filters.value.proOnly) count++;
    return count;
  }

  function clearAdvancedFilters() {
    filters.value.maxPrice = null;
    filters.value.policies.pets = false;
    filters.value.policies.couples = false;
    filters.value.maxMinimumStay = null;
    filters.value.services.internet = false;
    filters.value.services.cleaning = false;
    filters.value.services.allIncluded = false;
    filters.value.minBathrooms = null;
    filters.value.accommodationType.building = false;
    filters.value.accommodationType.apartment = false;
    filters.value.providers = [];
    filters.value.forStudents = false;
    filters.value.proOnly = false;

    if (route.params?.priceSlug) {
      const cityParam = route.params?.city;
      const isEN = route.path.startsWith('/findyourcoliving');
      const base = isEN ? '/findyourcoliving' : '/encuentratucoliving';
      router.push(cityParam ? `${base}/${cityParam}` : base);
    } else if (route.query?.maxPrice) {
      const isEN = route.path.startsWith('/findyourcoliving');
      const base = isEN ? '/findyourcoliving' : '/encuentratucoliving';
      router.push(base);
      performSearch();
    } else {
      performSearch();
    }
  }

  return {
    pillPriceOptionsForQuick,
    pillPriceLabel,
    pillStayOptions,
    pillStayLabel,
    pillMoreCount,
    pillMoreActive,
    togglePillDropdown,
    selectPillPrice,
    selectPillStay,
    quickToggleFilter,
    applyLocationFilter,
    applyLocationFilterMobile,
    applyAdvancedFilters,
    getLocationDisplay,
    getActiveFiltersCount,
    clearAdvancedFilters,
  };
}
